import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react'
import { orderingFetch } from '../lib/ordering-fetch'
import type { User } from '../types/ordering'

interface AuthContextType {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  isAdmin: boolean
  isViewOnly: boolean
  isOfficeSupport: boolean
  isSalesRep: boolean
  canManageQuotes: boolean
  canAccessAdminData: boolean
  login: (email: string, password: string) => Promise<void>
  logout: () => Promise<void>
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  isAuthenticated: false,
  isLoading: true,
  isAdmin: false,
  isViewOnly: false,
  isOfficeSupport: false,
  isSalesRep: false,
  canManageQuotes: false,
  canAccessAdminData: false,
  login: async () => {},
  logout: async () => {},
})

export function useAuth() {
  return useContext(AuthContext)
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  const isAuthenticated = user !== null
  const isAdmin = user?.role === 'admin'
  const isViewOnly = user?.role === 'view_only'
  const isOfficeSupport = user?.role === 'office_support'
  const isSalesRep = user?.role === 'sales_rep'
  const canManageQuotes = isAdmin || isSalesRep || isOfficeSupport
  const canAccessAdminData = isAdmin

  // Fetch session on mount
  useEffect(() => {
    let cancelled = false
    async function fetchSession() {
      try {
        const res = await orderingFetch('/api/auth/session')
        if (res.ok) {
          const data = await res.json()
          if (!cancelled) setUser(data.user)
        }
      } catch {
        // Session not available — user is not logged in
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }
    fetchSession()
    return () => { cancelled = true }
  }, [])

  const login = useCallback(async (email: string, password: string) => {
    const res = await orderingFetch('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    })
    if (!res.ok) {
      const err = await res.json().catch(() => ({ detail: 'Login failed' }))
      throw new Error(err.detail || 'Login failed')
    }
    const data = await res.json()
    setUser(data.user)
  }, [])

  const logout = useCallback(async () => {
    await orderingFetch('/api/auth/logout', { method: 'POST' })
    setUser(null)
  }, [])

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        isLoading,
        isAdmin,
        isViewOnly,
        isOfficeSupport,
        isSalesRep,
        canManageQuotes,
        canAccessAdminData,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}
